/**
 * Loan-type (product) vocabulary — single source of truth for the frontend.
 *
 * Live values come from the backend's `loan_type.collection_category` column
 * (see `app/crud/loan_type_filter.py`), surfaced as `loan_type` on application
 * rows and as the loan-type filter options. The DB is authoritative: a new
 * category row appears in the API without any frontend change, so nothing here
 * treats the known set as closed — unrecognised values fall through to a
 * neutral tone and render verbatim.
 *
 * Legacy aliases ("3 Wheeler", "2W", "L5 Goods", …) still arrive from the LOS
 * Excel export used by the Business Dashboard, so they stay mapped.
 */

/** Known collection categories, in filter-display order. NOT exhaustive. */
export const COLLECTION_CATEGORIES = ['E2W', 'L3', 'L5', 'Battery'] as const;

export type LoanTypeCategory = (typeof COLLECTION_CATEGORIES)[number];

/** Colour buckets. '3w' stays distinct from 'l3' to preserve existing badges. */
export type LoanTypeTone = 'e2w' | '3w' | 'l3' | 'l4' | 'l5' | 'battery' | 'unknown';

/**
 * Classify any loan-type string — category, alias, or free text from the LOS
 * export — into a colour bucket.
 */
export function loanTypeTone(loanType: string): LoanTypeTone {
  const n = loanType.trim().toLowerCase();
  if (!n) return 'unknown';
  if (n === 'e2w' || n === '2w' || n === 'ice2w') return 'e2w';
  if (n === '3 wheeler' || n === '3w') return '3w';
  if (n.includes('l3')) return 'l3';
  if (n.includes('l4')) return 'l4';
  if (n.includes('l5')) return 'l5';
  if (n.includes('battery')) return 'battery';
  return 'unknown';
}

/**
 * Short product label for badges and detail rows: 'E2W' -> '2W',
 * '3 Wheeler' -> '3W', 'L5 Passenger' -> 'L5'. Unrecognised values are
 * returned trimmed but otherwise untouched.
 */
export function shortLoanTypeLabel(loanType: string): string {
  const trimmed = loanType.trim();
  switch (loanTypeTone(trimmed)) {
    case 'e2w':
      return '2W';
    case '3w':
      return '3W';
    case 'l3':
      return 'L3';
    case 'l4':
      return 'L4';
    case 'l5':
      return 'L5';
    case 'battery':
      return 'Battery';
    default:
      return trimmed;
  }
}
